import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { FC, useEffect, useState } from 'react';
import {
  ScrollView,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';

import { getCalendarItemCoords } from '$tablet-app/src/app/calendar/common/actions';
import { CalendarItemUnion } from '$tablet-app/src/app/calendar/types';
import RecordImage from '$tablet-app/src/app/images/components/RecordImage';
import MapView from '$tablet-app/src/app/maps/MapView';

import { Recipe } from '$tablet-app/src/app/recipes/types';
import {
  layout,
  margin,
  padding,
  palette,
  spacing,
} from '$tablet-app/src/app/theme';
import { isRecurringItem } from '@akin-home/calendar';
import { getCalendarItemRecurrenceText } from '@akin-home/calendar/combined';
import { useListAllRecipeModels } from '@akin-home/recipes/react';
import { getOriginIDFromRecurrenceID } from '@akin-home/recurrence';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import styled from 'styled-components/native';
import {
  CalendarItem,
  CalendarItemType,
  CheckListItem,
  setChecklistItem,
} from '../../models';
import CalendarItemCheckbox from '../CalendarItemCheckbox/CalendarItemCheckbox';
import { CalendarItemDeleteConfirmation } from '../CalendarItemDeleteConfirmation/CalendarItemDeleteConfirmation';
import CalendarItemRecurrenceDeleteConfirmation from '../CalendarItemRecurrenceDeleteConfirmation';
import { EmojiWithBackground } from '../EmojiWithBackground/EmojiWithBackground';
import Line from '../Line';
import { ProfileIcon } from '../ProfileIcon/ProfileIcon';
import RecipePanel from '../RecipePanel';
import Row from '../Row';
import { SelectorPill } from '../SelectorPill/SelectorPill';
import { Text } from '../Text/Text';
import { CalendarItemDetailPresets } from './CalendarItemDetail.presets';
import CalendarItemDetailsGoalSection from './CalendarItemDetailsGoalSection';
import CalendarItemDetailsHeader from './common/CalendarItemDetailsHeader';
import CalendarItemTimeDisplayWhite from './common/CalendarItemTimeDisplay/CalendarItemTimeDisplayWhite';

const MAP_DIMENSIONS = { width: 400, height: 300 };

export interface CalendarItemDetailWidgetProps {
  item: CalendarItemUnion;
  dismiss?: () => void;
  edit?: () => void;
  remove?: (
    id: string,
    type: CalendarItemType,
    refresh_cache?: boolean
  ) => void;
  visible?: boolean;
  preset?: CalendarItemDetailPresets;
  onChecklistPress?: (
    calendarItem: CalendarItemUnion,
    item: CheckListItem
  ) => void;
  onPressRecipe?: (recipe: Recipe) => void;
  style?: StyleProp<ViewStyle>;
}

/* COPY OF CalendarItemDetail FOR PIXI CHAT WIDGET */

const CalendarItemDetailWidget: FC<CalendarItemDetailWidgetProps> = (props) => {
  const { item: itemDefaults, edit, dismiss, remove, onPressRecipe } = props;

  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [item, setItem] = useState<CalendarItemUnion>(itemDefaults);

  const recipe_ids = 'recipes' in item ? item.recipes.map((r) => r.id) : [];

  const { recipes } = useListAllRecipeModels({
    params: {
      filter: {
        or: recipe_ids.map((id) => ({ id: { eq: id } })),
      },
    },
    options: {
      enabled: item.type === 'Meal' && item.recipes?.length > 0,
    },
  });

  const showChecklist =
    item.type !== 'Meal' && (item.checklist?.length ?? 0) > 0;
  const coords = getCalendarItemCoords(item);

  const showHeader = edit || dismiss || remove;

  const onDeleteConfirm = () => remove && remove(item.id, item.type);

  const onDeleteRecurringSeries = () => {
    if (isRecurringItem(item)) {
      remove?.(getOriginIDFromRecurrenceID(item.id) as string, item.type, true);
    }
  };

  const onPressRecipeTile = (recipe: Recipe) => () => {
    onPressRecipe && onPressRecipe(recipe);
    dismiss?.();
  };

  const onDeleteRecurrenceInstance = () => {
    remove?.(item.id, item.type);
  };

  const onChecklistPress = (
    calendarItem: CalendarItem,
    checklistItem: CheckListItem
  ) => {
    setItem(setChecklistItem(item, checklistItem));
    props.onChecklistPress?.(item, checklistItem);
  };

  const toggleDeleteModal = () => setDeleteModalVisible(!deleteModalVisible);

  const onDeleteCancel = () => toggleDeleteModal();

  useEffect(() => {
    setItem(itemDefaults);
  }, [itemDefaults]);

  return (
    <>
      {item.isRecurrence ? (
        <CalendarItemRecurrenceDeleteConfirmation
          visible={deleteModalVisible}
          onConfirmDeleteAll={onDeleteRecurringSeries}
          onConfirmDeleteSingle={onDeleteRecurrenceInstance}
          onCancel={onDeleteCancel}
          item={item}
        />
      ) : (
        <CalendarItemDeleteConfirmation
          visible={deleteModalVisible}
          onConfirm={onDeleteConfirm}
          onCancel={onDeleteCancel}
          item={item}
        />
      )}

      <View style={StyleSheet.flatten([styles.contentContainer, props.style])}>
        {showHeader ? (
          <CalendarItemDetailsHeader
            onPressCancel={dismiss}
            onPressDelete={toggleDeleteModal}
            /** We are now only allowing meals to be edited from the meals section */
            onPressEdit={item.type !== 'Meal' ? edit : undefined}
          />
        ) : null}

        <ScrollView>
          <View style={padding(10, 0)}>
            <Row
              alignItems="center"
              marginBottom={spacing[5]}
              flexWrap="wrap"
              rowGap={spacing[6]}
            >
              <Row
                alignItems="center"
                flexWrap="wrap"
                minWidth={'76%'}
                maxWidth={'100%'}
                gap={spacing[6]}
              >
                {item.emoji ? (
                  <EmojiWithBackground
                    emoji={item.emoji}
                    preset={item.type}
                    style={{ backgroundColor: 'white' }}
                  />
                ) : null}

                <Row width={'50%'} flexGrow={1}>
                  <WhiteText
                    preset="h3"
                    style={{
                      color: 'white',
                      textAlign: 'left',
                      maxWidth: '100%',
                    }}
                  >
                    {item.title}
                  </WhiteText>
                </Row>
              </Row>

              <SelectorPill
                label={item.type}
                preset={item.type.toLowerCase()}
                selected
                hideTick
                style={{
                  paddingHorizontal: spacing[2],
                  paddingVertical: spacing[1],
                }}
                color="white"
              />
            </Row>

            <Row
              justifyContent="space-between"
              flexWrap="wrap"
              rowGap={spacing[6]}
            >
              <CalendarItemTimeDisplayWhite item={item} />

              <RecordImage record={item} style={styles.image} />
            </Row>
          </View>

          <Line style={margin(10, 0)} color={palette.lightGrey1} />

          {item.type === 'Meal'
            ? recipes.map((recipe) => (
                <RecipePanel
                  key={recipe.id}
                  recipe={recipe}
                  onClick={onPressRecipeTile(recipe)}
                  style={{ backgroundColor: palette.lightGrey }}
                />
              ))
            : null}
          <View style={padding(10, 0)}>
            <RowWithIcon icon={['far', 'sync']}>
              <WhiteText>{getCalendarItemRecurrenceText(item)}</WhiteText>
            </RowWithIcon>

            {'participants' in item &&
            item.participants &&
            item.participants.length > 0 ? (
              <RowWithIcon icon="user">
                <View>
                  <View style={margin(0, 0, 5, 0)}>
                    <WhiteText preset="bold">Who is taking part?</WhiteText>
                  </View>

                  {item.participants.map((participant, index) => (
                    <Row
                      key={index}
                      alignItems="center"
                      marginBottom={spacing[2]}
                    >
                      <View>
                        <ProfileIcon profile={participant} />
                      </View>

                      <WhiteText style={margin(0, 5)}>
                        {participant.name}
                      </WhiteText>
                    </Row>
                  ))}
                </View>
              </RowWithIcon>
            ) : null}

            {showChecklist ? (
              <RowWithIcon icon="tasks">
                <View>
                  <View style={margin(0, 0, 5, 0)}>
                    <WhiteText preset="bold">
                      What do you need to do or bring?
                    </WhiteText>
                  </View>

                  {item.checklist?.map((checklistItem) => (
                    <View key={item.id + checklistItem.id} style={margin(3, 0)}>
                      <CalendarItemCheckbox
                        calendarItem={item}
                        checklistItem={checklistItem}
                        onChange={onChecklistPress}
                      />
                    </View>
                  ))}
                </View>
              </RowWithIcon>
            ) : null}

            {item.notes ? (
              <RowWithIcon icon="user">
                <View>
                  <WhiteText preset="bold">Note</WhiteText>
                  <WhiteText>{item.notes}</WhiteText>
                </View>
              </RowWithIcon>
            ) : null}

            {coords ? (
              <Row marginBottom={10}>
                <MapView style={MAP_DIMENSIONS} {...coords} />
              </Row>
            ) : null}

            {item.type === 'Alert' && item.goalID ? (
              <GoalSectionWrapper>
                <CalendarItemDetailsGoalSection
                  goal_id={item.goalID}
                  date={item.start}
                />
              </GoalSectionWrapper>
            ) : null}
          </View>
        </ScrollView>
      </View>
    </>
  );
};
interface RowWithIconProps {
  icon: IconProp;
  children?: React.ReactNode;
}

const RowWithIcon: FC<RowWithIconProps> = (props) => (
  <Row marginBottom={spacing[5]}>
    <FontAwesomeIcon
      style={margin(5, 15, 0, 0)}
      icon={props.icon}
      color="white"
    />
    <View style={layout.flex_1}>{props.children}</View>
  </Row>
);

const styles = StyleSheet.create({
  contentContainer: {
    ...padding(0, 0, 10, 0),
    height: '100%',
    color: 'white',
  },
  image: { height: 80, width: 160, borderRadius: 10, maxWidth: '100%' },
});

const GoalSectionWrapper = styled.View`
  flex-direction: row;
  justify-content: center;
  padding-top: 16px;
`;

const WhiteText = styled(Text)`
  color: white;
`;

export { CalendarItemDetailWidget };
